import { useState, useEffect } from "react";
import "./assignProperties.css";
import { motion } from "framer-motion";
import {
  Search,
  User,
  Building2,
  BookOpen,
  Users,
  UserCog,
} from "lucide-react";
import axios from "axios";

import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import { API_ENDPOINT } from "../../../../config/apiEndpoint";

/** API may send userId (camelCase) or UserId (PascalCase) depending on client/config */
const resolveNumericUserId = (user) => {
  const raw = user?.userId ?? user?.UserId;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const normDeptRow = (d) => ({
  departmentId: d.departmentId ?? d.DepartmentId,
  departmentName: d.departmentName ?? d.DepartmentName,
  isAssigned: Boolean(d.isAssigned ?? d.IsAssigned),
});

const normModuleRow = (m) => ({
  moduleId: m.moduleId ?? m.ModuleId,
  moduleName: m.moduleName ?? m.ModuleName,
  moduleCode: m.moduleCode ?? m.ModuleCode,
  department: m.department ?? m.Department,
  isAssigned: Boolean(m.isAssigned ?? m.IsAssigned),
});

const normGroupRow = (g) => ({
  groupId: g.groupId ?? g.GroupId,
  groupName: g.groupName ?? g.GroupName,
  totalStudents: g.totalStudents ?? g.TotalStudents,
  moduleName: g.moduleName ?? g.ModuleName,
  moduleCode: g.moduleCode ?? g.ModuleCode,
  moduleId: g.moduleId ?? g.ModuleId,
  isAssigned: Boolean(g.isAssigned ?? g.IsAssigned),
});

const AssignAcademicProperties = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [modules, setModules] = useState([]);
  const [studentGroups, setStudentGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [selectedUser, setSelectedUser] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [assignmentLoading, setAssignmentLoading] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(
          `${API_ENDPOINT}/api/UserGetters/GetAllLecturersAndReviewers`
        );
        if (response?.data?.status) {
          const raw = response.data.users || [];
          setUsers(
            raw.map((u) => ({
              ...u,
              userId: resolveNumericUserId(u) ?? u?.userId ?? u?.UserId,
            })),
          );
          setLoading(false);
        } else {
          console.log(response.data.message);
          setUsers(null);
        }
      } catch (error) {
        console.log("An error occurred. " + error);
        setUsers(null);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
    const storedRole = sessionStorage.getItem("userRole");
    if (storedRole === "ADMIN" || storedRole === "LEAD-REVIEWER") {
      setIsAdmin(true);
    }
  }, [API_ENDPOINT]);
let filteredUsers;
  if (users) {
  filteredUsers = users.filter((user) => {
    const matchSearch =
      (user.userName || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (user.userEmail || "").toLowerCase().includes(searchQuery.toLowerCase());
    const matchRole =
      roleFilter === "all" ||
      user.userRole === roleFilter ||
      (roleFilter === "LEAD-REVIEWER" && user.userRole === "LEAD-REVIEWER"); // Added
    return matchSearch && matchRole;
  });
}

  const axiosAssignGet = (url, params) =>
    axios.get(url, {
      params,
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
      validateStatus: () => true,
    });

  const fetchDepartments = async (id) => {
    try {
      const response = await axiosAssignGet(
        `${API_ENDPOINT}/api/AdminGetUserProfile/GetLecturerAvailableDepartments`,
        { userId: id },
      );

      const list = response?.data?.departments ?? response?.data?.Departments;
      const rows = Array.isArray(list) ? list.map(normDeptRow) : [];
      if (response.status >= 400) {
        console.warn("GetLecturerAvailableDepartments HTTP", response.status, response.data);
        toast.error(
          response.data?.message || `Could not load departments (HTTP ${response.status}).`,
        );
        setDepartments([]);
        return;
      }
      if (response?.data?.status !== false) {
        setDepartments(rows);
      } else {
        console.log(response?.data?.message || "Failed to fetch departments");
        setDepartments(rows);
      }
    } catch (error) {
      console.log(error.response?.data?.message || "An error occurred");
      setDepartments([]);
    }
  };
  const fetchModules = async (id) => {
    try {
      const response = await axiosAssignGet(
        `${API_ENDPOINT}/api/AdminGetUserProfile/GetLecturerAvailablebleModules`,
        { userId: id },
      );

      const list = response?.data?.modules ?? response?.data?.Modules;
      const rows = Array.isArray(list) ? list.map(normModuleRow) : [];
      if (response.status >= 400) {
        console.warn("GetLecturerAvailablebleModules HTTP", response.status, response.data);
        toast.error(
          response.data?.message || `Could not load modules (HTTP ${response.status}).`,
        );
        setModules([]);
        return;
      }
      if (response?.data?.status !== false) {
        setModules(rows);
      } else {
        console.log(response?.data?.message || "Failed to fetch modules");
        setModules(rows);
      }
    } catch (error) {
      console.log(error.response?.data?.message || "An error occurred");
      setModules([]);
    }
  };
  const fetchGroups = async (id) => {
    try {
      const response = await axiosAssignGet(
        `${API_ENDPOINT}/api/AdminGetUserProfile/GetLecturerAvailablebleGroups`,
        { userId: id },
      );

      const list = response?.data?.groups ?? response?.data?.Groups;
      const rows = Array.isArray(list) ? list.map(normGroupRow) : [];
      if (response.status >= 400) {
        console.warn("GetLecturerAvailablebleGroups HTTP", response.status, response.data);
        toast.error(
          response.data?.message || `Could not load groups (HTTP ${response.status}).`,
        );
        setStudentGroups([]);
        return;
      }
      if (response?.data?.status !== false) {
        setStudentGroups(rows);
      } else {
        console.log(response?.data?.message || "Failed to fetch groups");
        setStudentGroups(rows);
      }
    } catch (error) {
      console.log(error.response?.data?.message || "An error occurred");
      setStudentGroups([]);
    }
  };

  const handleSelectUser = async (user) => {
    const uid = resolveNumericUserId(user);
    if (uid == null) {
      Swal.fire({
        title: "Invalid user",
        text: "Could not determine this user's id. Refresh the page or contact support.",
        icon: "warning",
        customClass: { popup: "my-swal-theme" },
      });
      return;
    }
    setSelectedUser({ ...user, userId: uid });
    setAssignmentLoading(true);
    setDepartments([]);
    setModules([]);
    setStudentGroups([]);
    try {
      await fetchDepartments(uid);
      if (user.userRole === "LECTURER") {
        await Promise.all([fetchModules(uid), fetchGroups(uid)]);
      } else {
        setModules([]);
        setStudentGroups([]);
      }
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleAssignDept = async (userId, departmentId) => {
    try {
      const reviewerUserId = userId;
      const reviewerDepartmentId = departmentId;
      const response = await axios.post(
        `${API_ENDPOINT}/api/Assigning/AssignDepartmentToUser`,
        { userId: reviewerUserId, departmentId: reviewerDepartmentId },
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
          validateStatus: () => true,
        },
      );

      if (response?.data?.status) {
        Swal.fire({
          title: "Success",
          text: response.data.message,
          icon: "success",
          customClass: { popup: "my-swal-theme" },
          timer: 1500,
        });
        if (userId) {
          fetchDepartments(userId);
          fetchModules(userId);
          fetchGroups(userId);
        }
      } else {
        Swal.fire({
          title: "Error",
          text: response.data.message,
          icon: "error",
          customClass: { popup: "my-swal-theme" },
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Failed!",
        text: "An error occurred",
        icon: "error",
        customClass: { popup: "my-swal-theme" },
      });
    }
  };

  const handleRemoveDept = async (userId, departmentId) => {
    try {
      const reviewerUserId = userId;
      const reviewerDepartmentId = departmentId;
      console.log(reviewerUserId + " " + reviewerDepartmentId);
      const response = await axios.post(
        `${API_ENDPOINT}/api/Assigning/UnassignDepartmentFromUser`,
        { userId: reviewerUserId, departmentId: reviewerDepartmentId },
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
          validateStatus: () => true,
        },
      );

      if (response?.data?.status) {
        Swal.fire({
          title: "Success",
          text: response.data.message,
          icon: "success",
          customClass: { popup: "my-swal-theme" },
          timer: 1500,
        });
        if (userId) {
          fetchDepartments(userId);
          fetchModules(userId);
          fetchGroups(userId);
        }
      } else {
        Swal.fire({
          title: "Error",
          text: response.data.message,
          icon: "error",
          customClass: { popup: "my-swal-theme" },
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Failed!",
        text: "An error occurred",
        icon: "error",
        customClass: { popup: "my-swal-theme" },
      });
    }
  };

  const handleAssignModule = async (userId, moduleId) => {
    try {
      const lecturerUserId = userId;
      const lecturerModuleId = moduleId;
      const response = await axios.post(
        `${API_ENDPOINT}/api/Assigning/AssignModuleToUser`,
        { userId: lecturerUserId, moduleId: lecturerModuleId },
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
          validateStatus: () => true,
        },
      );

      if (response?.data?.status) {
        Swal.fire({
          title: "Success",
          text: response.data.message,
          icon: "success",
          customClass: { popup: "my-swal-theme" },
          timer: 1500,
        });
        if (userId) {
          fetchDepartments(userId);
          fetchModules(userId);
          fetchGroups(userId);
        }
      } else {
        Swal.fire({
          title: "Error",
          text: response.data.message,
          icon: "error",
          customClass: { popup: "my-swal-theme" },
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Failed!",
        text: "An error occurred",
        icon: "error",
        customClass: { popup: "my-swal-theme" },
      });
    }
  };

  const handleRemoveModule = async (userId, moduleId) => {
    try {
      const lecturerUserId = userId;
      const lecturerModuleId = moduleId;
      const response = await axios.post(
        `${API_ENDPOINT}/api/Assigning/UnassignModuleFromUser`,
        { userId: lecturerUserId, moduleId: lecturerModuleId },
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
          validateStatus: () => true,
        },
      );

      if (response?.data?.status) {
        Swal.fire({
          title: "Success",
          text: response.data.message,
          icon: "success",
          customClass: { popup: "my-swal-theme" },
          timer: 1500,
        });
        if (userId) {
          fetchDepartments(userId);
          fetchModules(userId);
          fetchGroups(userId);
        }
      } else {
        Swal.fire({
          title: "Error",
          text: response.data.message,
          icon: "error",
          customClass: { popup: "my-swal-theme" },
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Failed!",
        text: "An error occurred",
        icon: "error",
        customClass: { popup: "my-swal-theme" },
      });
    }
  };

  const handleAssignGroup = async (userId, moduleId, groupName) => {
    try {
      const lecturerUserId = userId;

      const response = await axios.post(
        `${API_ENDPOINT}/api/Assigning/AssignGroupsToUserModule`,
        {
          userId: lecturerUserId,
          moduleId: moduleId,
          groups: [groupName],
        },
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
          validateStatus: () => true,
        },
        
      );

      if (response?.data?.status) {
        Swal.fire({
          title: "Success",
          text: response.data.message,
          icon: "success",
          customClass: { popup: "my-swal-theme" },
          timer: 1500,
        });
        if (userId) {
          fetchDepartments(userId);
          fetchModules(userId);
          fetchGroups(userId);
        }
      } else {
        Swal.fire({
          title: "Error",
          text: response.data.message,
          icon: "error",
          customClass: { popup: "my-swal-theme" },
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Failed!",
        text: "An error occurred",
        icon: "error",
        customClass: { popup: "my-swal-theme" },
      });
    }
  };

  const handleRemoveGroup = async (userId, moduleId, groupName) => {
    try {
      const lecturerUserId = userId;
      const response = await axios.post(
        `${API_ENDPOINT}/api/Assigning/UnassignGroupsFromUserModule`,
        {
          userId: lecturerUserId,
          moduleId: moduleId,
          groups: [groupName],
        },
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
          validateStatus: () => true,
        },
        
      );

      if (response?.data?.status) {
        Swal.fire({
          title: "Success",
          text: response.data.message,
          icon: "success",
          customClass: { popup: "my-swal-theme" },
          timer: 1500,
        });
        if (userId) {
          fetchDepartments(userId);
          fetchModules(userId);
          fetchGroups(userId);
        }
      } else {
        Swal.fire({
          title: "Error",
          text: response.data.message,
          icon: "error",
          customClass: { popup: "my-swal-theme" },
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Failed!",
        text: "An error occurred",
        icon: "error",
        customClass: { popup: "my-swal-theme" },
      });
    }
  };

  const handleViewFullUser = (userId, role) => {
    if (role === "LECTURER") {
      navigate(`/assign/lecturer/${userId}`);
    } else {
      navigate(`/assign/reviewer/${userId}`);
    }
  };

  if (loading) return <div className="loader">Loading...</div>;
  return (
    <div className="assign-academic-properties-container">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="assign-academic-properties-header"
      >
        <div>
          <h1>Assign Academic Properties</h1>
          <p>Assign departments, modules, and student groups to users</p>
        </div>
        {selectedUser?.userId && isAdmin && (
          <button
            className="full-user-button"
            onClick={() =>
              handleViewFullUser(selectedUser.userId, selectedUser.userRole)
            }
          >
            <UserCog size={16} /> View Full User
          </button>
        )}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="assign-academic-properties-filters"
      >
        <div className="lecturers-list-search-container">
          <div className="admin-search-box">
            <div className="admin-search-icon">
              <Search className="icon" />
            </div>
            <input
              type="search"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="admin-dropdown"
          >
            <option value="all">All Roles</option>
            <option value="LECTURER">Lecturers</option>
            <option value="REVIEWER">Reviewers</option>
            <option value="LEAD-REVIEWER">Lead-Reviewers</option>
          </select>
          <button className="admin-filter-button">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 4h18M6 8h12M10 12h4M12 16h0" />
            </svg>
            Filter
          </button>
        </div>
      </motion.div>

      <div className="assign-academic-properties-content">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="assign-academic-properties-users"
        >
          <div className="assign-academic-properties-header">
            <h2>Users</h2>
          </div>

          <div className="assign-academic-users-list-container">
            {filteredUsers && filteredUsers.length > 0 ? (
              filteredUsers.map((user) => (
                <div
                  key={user.userId}
                  className={`user-info-card  user-item ${selectedUser.userId === user.userId ? "selected" : ""
                    }`}
                  onClick={() => handleSelectUser(user)}
                >
                  <img
                    src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                      `${user.userName?.charAt(0) || ""}${user.userSurname?.charAt(0) || ""
                      }` || "User"
                    )}&background=random`}
                    alt={user.userName}
                  />
                  <div className="user-card-information">
                    <h2>{user.userName}</h2>
                    <span>{user.userEmail}</span>
                    <span className="role">{user.userRole}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-users-found">
                <p>No users found.</p>
              </div>
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="assign-academic-properties-interface"
        >
          {selectedUser && selectedUser.userRole ? (
            <>
              <div className="selected-user-info-container">
                <div className="selected-user-info-content">
                  <img
                    src={
                      selectedUser.avatar ||
                      `https://ui-avatars.com/api/?name=${encodeURIComponent(
                        `${selectedUser.userName?.charAt(0) || ""}${selectedUser.userSurname?.charAt(0) || ""
                        }` || "User"
                      )}&background=random`
                    }
                    alt={selectedUser.userName}
                    className="edit-user-profile-pic"
                  />

                  <div className="selected-user-info-text">
                    <h2>
                      {selectedUser.userName} {selectedUser.userSurname}{" "}
                    </h2>
                    <span className="email">{selectedUser.userEmail}</span>
                    <p className={`role ${selectedUser.userRole}`}>
                      {selectedUser.userRole}
                    </p>
                  </div>
                </div>
              </div>
              {assignmentLoading && (
                <p className="assign-academic-loading" role="status">
                  Loading departments, modules, and groups…
                </p>
              )}
              <div className="assign-academic-properties-section">
                <div className="academic-properties-header">
                  <Building2
                    size={25}
                    className="assign-academic-properties-icon departments"
                  />
                  <h3>Departments</h3>
                </div>

                <div className="assign-academic-properties-options departments">
                  {departments.map((dept) => {
                    return (
                      <div
                        className="assign-property-card department"
                        key={dept.departmentId || dept.departmentName}
                      >
                        <label
                          className={`option ${dept.isAssigned ? "checked" : ""
                            }`}
                        >
                          <input
                            type="checkbox"
                            checked={dept.isAssigned}
                            onChange={() =>
                              dept.isAssigned
                                ? handleRemoveDept(
                                  selectedUser.userId,
                                  dept.departmentId
                                )
                                : handleAssignDept(
                                  selectedUser.userId,
                                  dept.departmentId
                                )
                            }
                          />
                          <strong>{dept.departmentName}</strong>
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>

              {selectedUser.userRole === "LECTURER" && (
                <div className="assign-academic-properties-section">
                  <div className="academic-properties-header">
                    <BookOpen
                      size={25}
                      className="assign-academic-properties-icon modules"
                    />
                    <h3>Modules</h3>
                  </div>

                  <div className="assign-academic-properties-options modules">
                    {modules.map((mod) => {
                      return (
                        <div
                          className="assign-property-card module"
                          key={mod.moduleId || mod.moduleCode}
                        >
                          <label
                            className={`option ${mod.isAssigned ? "checked" : ""
                              }`}
                          >
                            <input
                              type="checkbox"
                              checked={mod.isAssigned}
                              onChange={() =>
                                mod.isAssigned
                                  ? handleRemoveModule(
                                    selectedUser.userId,
                                    mod.moduleId
                                  )
                                  : handleAssignModule(
                                    selectedUser.userId,
                                    mod.moduleId
                                  )
                              }
                            />
                            <strong>{mod.moduleCode}:</strong>{" "}
                            <span>{mod.moduleName}</span>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {selectedUser.userRole === "LECTURER" && (
                <div className="assign-academic-properties-section">
                  <div className="academic-properties-header">
                    <Users
                      size={25}
                      className="assign-academic-properties-icon groups"
                    />
                    <h3>Student Groups</h3>
                  </div>

                  <div className="assign-academic-properties-options groups">
                    {studentGroups.map((group) => {
                      return (
                        <div
                          className="assign-property-card group"
                          key={`${group.moduleId}-${group.groupId}-${group.groupName}`}
                        >
                          <label
                            className={`option ${group.isAssigned ? "checked" : ""
                              }`}
                          >
                            <input
                              type="checkbox"
                              checked={group.isAssigned}
                              onChange={() =>
                                group.isAssigned
                                  ? handleRemoveGroup(
                                    selectedUser.userId,
                                    group.moduleId,
                                    group.groupName
                                  )
                                  : handleAssignGroup(
                                    selectedUser.userId,
                                    group.moduleId,
                                    group.groupName
                                  )
                              }
                            />
                            <strong>
                              {group.moduleCode}: Group - {group.groupName}:
                            </strong>{" "}
                            <span>{group.totalStudents} students</span>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="no-user-selected">
              <User size={36} />
              <p>Select a user to manage their academic assignments.</p>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AssignAcademicProperties;
